import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../../Loading/Loading";
import Table from "../Table/Table";
import "./AssignmentsPage.css";
import ClassroomAssignmentDetails from "../../../../interfaces/ClassroomAssignmentDetails";
import Assignment from "../../../../interfaces/Assignment";
import firebase from "../../../../services/firebase/FirebaseService";
import { formatDate, sortDates, sortNumbers } from "../Table/TableSortAndFormatFunctions";
import Submission from "../../../../interfaces/Submission";
import AssignmentOnTeacher from "../../../../state/teacherData/AssignmentOnTeacher";
import { useLocation } from "react-router";
import { FormControl, InputLabel, MenuItem, Select, Button, Hidden, capitalize } from "@material-ui/core";
import Classroom from "../../../../interfaces/Classroom";
import { useHistory } from "react-router-dom";
import FilterIcon from "../../../../assets/icons/Filter_By_Class_Icon_orange.svg";
import add from "../../../../assets/icons/noun_Plus_2190.png";
import EmptyState from "../../../EmptyState";
import queryString from "query-string";
import { checkFilterByParam } from "../TeacherHomeScreen/FilteringFromURLFunctions";

interface AssignmentClasses {
  [key: string]: {
    [key: string]: ClassroomAssignmentDetails;
  };
}

interface AssignmentsPageProps {
  assignments: AssignmentOnTeacher[];
  assignmentClasses: AssignmentClasses;
  classrooms: Classroom[];
}

interface AssignmentInfo extends Object {
  assignment: Assignment;
  totalWords: number;
  submitted: number[];
}

const createAssignmentLinkURLFunc =
  (classId?: string) =>
  ({ original: { assignment } }: any) =>
    `/Assignments/${assignment.id}${classId ? `?class=${classId}` : ""}`;
const createHeaderText = (filterBy?: string) =>
  filterBy && ["starred", "shared", "archived"].includes(filterBy)
    ? `${capitalize(filterBy)} Assignments`
    : "Assignments";

const AssignmentsPage = (props: AssignmentsPageProps) => {
  const history = useHistory();
  const location = useLocation();
  // Get attributes filter and classId from url
  // As query params
  const parsed = queryString.parse(location.search);
  const filterByQueryParam =
    typeof parsed.filterBy === "string" && checkFilterByParam(parsed.filterBy) ? parsed.filterBy : undefined;
  const classIdQueryParam = typeof parsed.classId === "string" ? parsed.classId : undefined;
  const filterBy = filterByQueryParam;
  const classId = classIdQueryParam;

  // Track loading state
  const [isLoading, setIsLoading] = useState(true);
  const [studentSubmissions, setStudentSubmissions] = useState<
    Record<string, Record<string, Record<string, Submission>>>
  >({});
  const [assignmentInfo, setAssignmentInfo] = useState<Record<string, AssignmentInfo>>({});
  const submissionWatches = useRef<Record<string, () => void>>({});
  const [runCount, setRunCount] = useState(true);

  const watchStudentSubmission =
    (assignmentId: string, classroom: string, studentId: string) => async (submission: Submission) =>
      setStudentSubmissions(state => ({
        ...state,
        [assignmentId]: {
          ...(state[assignmentId] || {}),
          [classroom]: {
            ...(state[assignmentId]?.[classroom] || {}),
            [studentId]: submission,
          },
        },
      }));

  useEffect(() => {
    setIsLoading(true);

    if (props.assignments) {
      const watchSubmissions = async (assignment: AssignmentOnTeacher) => {
        const assignmentId = assignment.id;
        if (!assignmentId) {
          return;
        }
        if (filterBy !== "archived") {
          let studentsAssigned = 0;
          Object.entries(props.assignmentClasses[assignmentId] || {})
            .filter(([key]) => !classId || key === classId)
            .forEach(([classroom, classroomObj]) => {
              Object.entries(classroomObj?.students || {})
                .filter(([_key, student]) => student)
                .forEach(([studentId]) => {
                  studentsAssigned++;
                  // If watch does not already exist
                  const bookId = assignment?.assignmentDetails?.bookId;
                  if (!submissionWatches.current[`${assignmentId}${classroom}${studentId}`]) {
                    submissionWatches.current[assignmentId + classroom + studentId] = firebase.watchSubmission(
                      studentId,
                      bookId,
                      watchStudentSubmission(assignmentId, classroom, studentId),
                    );
                  }
                });
            });
          if (assignment) {
            setAssignmentInfo(state => ({
              ...state,
              [assignmentId]: {
                assignment: assignment.assignmentDetails,
                totalWords: assignment.totalWords,
                submitted: [0, studentsAssigned],
              },
            }));
          }
        } else {
          if (assignment) {
            setAssignmentInfo(state => ({
              ...state,
              [assignmentId]: {
                assignment: assignment.assignmentDetails,
                totalWords: assignment.totalWords,
                submitted: [0, 0],
              },
            }));
          }
        }
      };

      setAssignmentInfo(state =>
        Object.entries(state).reduce(
          (acc, [key, info]) => (props.assignments.some(({ id }) => id === key) ? { ...acc, [key]: info } : acc),
          {},
        ),
      );
      props.assignments.forEach(watchSubmissions);
      setRunCount(state => !state);

      if (props.assignments.length === 0 || filterBy === "archived") {
        setIsLoading(false);
      }
    }

    const currentSubmissionWatches = submissionWatches.current;

    return () => {
      Object.entries(currentSubmissionWatches).forEach(([key, submission]) => {
        submission();
        delete currentSubmissionWatches[key];
      });
    };
  }, [props.assignments, props.assignmentClasses, classId, filterBy]);

  useEffect(() => {
    if (props.assignments.length && filterBy !== "archived") {
      props.assignments.forEach(assignment => {
        const assignmentId = assignment.id;

        let studentsSubmitted = 0;
        if (assignmentId) {
          if (classId) {
            const filteredSubmissionsToClass = studentSubmissions?.[assignmentId]?.[classId];
            studentsSubmitted = Object.entries(filteredSubmissionsToClass || {})
              .map(([_uid, submission]) => {
                return Number(submission ? Boolean(submission.turnedIn) : false);
              })
              .reduce((a, b) => a + b, 0);
          } else {
            studentsSubmitted = Object.entries(studentSubmissions?.[assignmentId] || {})
              .map(([_classId, submissions]) => Object.values(submissions))
              .flat()
              .map(submission => Number(submission ? Boolean(submission.turnedIn) : false))
              .reduce((a, b) => a + b, 0);
          }
        }

        setAssignmentInfo(state => {
          const info = state[assignmentId];
          return info && info.assignment
            ? {
                ...state,
                [assignmentId]: {
                  ...info,
                  submitted: [studentsSubmitted, info.submitted[1] || 0],
                },
              }
            : state;
        });
      });
      setIsLoading(false);
    }
  }, [studentSubmissions, runCount, classId, filterBy, props.assignmentClasses, props.assignments]);

  const columns = useMemo(
    () => [
      {
        Header: "Assignment",
        accessor: (data: any) => data.assignment.title || "",
        Cell: (props: { value: string }) => <div className="assignment">{props.value}</div>,
      },
      {
        Header: "Submissions",
        accessor: (data: any) => {
          if (!data.assignment.isActive) return "";
          return data.submitted[1] ? data.submitted[0] / data.submitted[1] : 0;
        },
        sortType: sortNumbers,
        Cell: (props: any) =>
          props.value || props.value === 0
            ? props.row.original.submitted[0] + "/" + props.row.original.submitted[1]
            : "",
      },
      {
        Header: "Word Count",
        accessor: (data: any) => (data?.totalWords === 0 ? "-" : data.totalWords),
        sortType: sortNumbers,
      },
      {
        Header: "Created",
        accessor: (data: any) => new Date(data.assignment.dateCreated || ""),
        sortType: sortDates,
        Cell: (props: { value: Date }) => formatDate(props.value),
      },
      {
        Header: "Last Modified",
        accessor: (data: any) => new Date(data.assignment.lastModified || ""),
        sortType: sortDates,
        Cell: (props: { value: Date }) => formatDate(props.value),
      },
    ],
    [],
  );

  const createAssignmentLinkURL = createAssignmentLinkURLFunc(classId);

  const handleClassroomFilter = (event: React.ChangeEvent<{ value: any }>) => {
    const classId = event.target.value;
    history.push(`/Assignments?${queryString.stringify({ filterBy: filterBy, classId: classId }, { skipNull: true })}`);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="AssignmentsPage">
      <div style={{ display: "flex", flexDirection: "row" }}>
        <h1>{createHeaderText(filterBy)}</h1>
        <Hidden xsDown>
          <div style={{ position: "absolute", right: "0" }}>
            <Button
              href="/teacher/#/assignmentSelection"
              style={{
                textTransform: "none",
                display: "flex",
                flexDirection: "row",
                marginRight: "50px",
              }}
            >
              <img style={{ color: "#FC6954" }} height={25} color="#FC6954" src={add} alt="Add Assignment" />
              <p
                style={{
                  color: "#FC6954",
                  fontWeight: "bold",
                  marginBottom: "13px",
                  marginLeft: "7.5px",
                  fontSize: "15.5px",
                }}
              >
                Add Assignment
              </p>
            </Button>
          </div>
        </Hidden>
      </div>
      <Hidden smUp>
        <Button
          href="/teacher/#/assignmentSelection"
          style={{
            textTransform: "none",
            display: "flex",
            flexDirection: "row",
            marginLeft: "-11px",
          }}
        >
          <img style={{ color: "#FC6954" }} height={25} color="#FC6954" src={add} alt="Add Assignment" />
          <p
            style={{
              color: "#FC6954",
              fontWeight: "bold",
              marginBottom: "13px",
              marginLeft: "7.5px",
              fontSize: "15.5px",
            }}
          >
            Add Assignment
          </p>
        </Button>
      </Hidden>
      {filterBy !== "archived" && (
        <FormControl variant="outlined">
          <InputLabel>
            <img className="FilterIcon" src={FilterIcon} aria-hidden alt="" />
            Filter By Class
          </InputLabel>
          {/* label dictates the length of the gap in the outline for when the label moves up */}
          {/* the 'xxl' is a stand in for the icon */}
          <Select label="xxlFilter By Class" value={classId ? classId : ""} onChange={handleClassroomFilter}>
            <MenuItem value="">All Classes</MenuItem>
            {props.classrooms &&
              props.classrooms.map(classroom => (
                <MenuItem value={classroom.id} key={classroom.id}>
                  {classroom.className}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )}
      {/* if no assignments show "no assignments," otherwise show table */}
      {props.assignments.length === 0 ? (
        <EmptyState>No Assignments Found</EmptyState>
      ) : props.assignments.length !== Object.values(assignmentInfo).length ? (
        <Loading />
      ) : (
        <div className="AssignmentTable">
          <Table
            kind="assignment"
            columns={columns}
            data={Object.values(assignmentInfo)}
            createRowLinkURL={createAssignmentLinkURL}
          />
        </div>
      )}
    </div>
  );
};
export default AssignmentsPage;
